import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle2, XCircle, AlertCircle, ClipboardList, Eye,
} from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";

const PRIORITY_TONES = {
  urgent: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-emerald-100 text-emerald-700",
};

export default function ApprovalQueue() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // {row, mode:'approve'|'reject'}
  const [form, setForm] = useState({
    remarks: "", voice: null, approved_amount: "", conditions: [],
    alternative: "", newCondition: "",
  });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/request-queue/approvals");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load queue");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    if (form.remarks.trim().length < 3) return toast.error("Remarks required (min 3 chars)");
    setBusy(true);
    try {
      const endpoint = decision.mode === "approve" ? "approve" : "reject";
      await api.post(`/requests/${decision.row.id}/${endpoint}`, {
        decision: decision.mode,
        remarks: form.remarks.trim(),
        voice_note_url: form.voice,
        approved_amount: form.approved_amount ? parseFloat(form.approved_amount) : null,
        conditions: form.conditions,
        alternative_suggestion: decision.mode === "reject" ? form.alternative.trim() || null : null,
      });
      toast.success(`Request ${decision.mode === "approve" ? "approved" : "rejected"}`);
      setDecision(null);
      setForm({ remarks: "", voice: null, approved_amount: "", conditions: [], alternative: "", newCondition: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="approval-queue">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Pending Approvals</div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Requests Awaiting Your Approval
        </h1>
        <p className="text-sm text-slate-500 mt-1">Approve, reject, or request changes. Decisions trigger emails to employee + downstream approvers.</p>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="empty-queue">
          <ClipboardList className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-600 font-medium">No requests awaiting your approval.</div>
          <div className="text-xs text-slate-400 mt-1">All clear ✓</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`queue-row-${r.request_number}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                    <span className="font-mono">{r.request_number}</span>
                    <span>·</span>
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${PRIORITY_TONES[r.priority]}`}>{r.priority}</span>
                    <span>·</span>
                    <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium">
                      Awaiting {(r.current_approval_level || "").toUpperCase()}
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-slate-900">{r.subject}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.employee_name} ({r.employee_punch_id}) · {r.request_type?.replace("_", " ")} · {r.category}
                  </div>
                  <div className="text-xs text-slate-600 mt-2 line-clamp-2">{r.description}</div>
                  {r.justification && (
                    <div className="text-xs text-amber-800 mt-2 border-l-2 border-amber-300 pl-2">
                      <b>Justification:</b> {r.justification}
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-xs mt-2 flex-wrap">
                    {r.estimated_cost && (
                      <span className="font-semibold text-slate-700">
                        ₹{Number(r.estimated_cost).toLocaleString("en-IN")}
                      </span>
                    )}
                    <span className="text-slate-500">Chain: {r.approval_chain?.join(" → ") || "—"}</span>
                    {r.manager_status === "approved" && <span className="text-emerald-700">✓ Manager</span>}
                    {r.hod_status === "approved" && <span className="text-emerald-700">✓ HOD</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/admin/requests/${r.id}`)} data-testid={`view-${r.request_number}`}>
                    <Eye className="h-3 w-3 mr-1" /> View
                  </Button>
                  <Button
                    size="sm"
                    className="brand-bg brand-bg-hover text-white"
                    onClick={() => setDecision({ row: r, mode: "approve" })}
                    data-testid={`approve-${r.request_number}`}
                  >
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    onClick={() => setDecision({ row: r, mode: "reject" })}
                    data-testid={`reject-${r.request_number}`}
                  >
                    <XCircle className="h-3 w-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent className="max-w-lg" data-testid="approval-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision?.mode === "approve" ? "✅ Approve Request" : "❌ Reject Request"}
              {decision && <span className="block text-xs text-slate-500 font-mono mt-0.5">{decision.row.request_number} — {decision.row.subject}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">
                Remarks * (min 3 chars)
              </label>
              <Textarea
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                rows={3}
                placeholder={decision?.mode === "approve" ? "E.g., Approved. Buy from approved vendor list." : "Clear reason for rejection..."}
                className="mt-1"
                data-testid="remarks-input"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Voice (optional)</label>
              <div className="mt-1">
                <VoiceRecorder
                  value={form.voice}
                  onChange={(url) => setForm({ ...form, voice: url || null })}
                  uploadEndpoint="/requests/attachments"
                  uploadFields={{}}
                  urlFromResponse={(d) => d.file_url}
                  testIdPrefix="approval-voice"
                  compact
                />
              </div>
            </div>
            {decision?.mode === "approve" && (
              <>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500">
                    Approved Amount (₹) — optional
                  </label>
                  <Input
                    type="number"
                    min="0"
                    placeholder={decision?.row?.estimated_cost ? `Original: ₹${decision.row.estimated_cost}` : "0"}
                    value={form.approved_amount}
                    onChange={(e) => setForm({ ...form, approved_amount: e.target.value })}
                    className="mt-1"
                    data-testid="approved-amount-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500">
                    Conditions (optional)
                  </label>
                  <div className="flex gap-1 mt-1">
                    <Input
                      placeholder="e.g., Get 3 vendor quotations"
                      value={form.newCondition}
                      onChange={(e) => setForm({ ...form, newCondition: e.target.value })}
                      data-testid="condition-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && form.newCondition.trim()) {
                          e.preventDefault();
                          setForm({ ...form, conditions: [...form.conditions, form.newCondition.trim()], newCondition: "" });
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (form.newCondition.trim()) {
                          setForm({ ...form, conditions: [...form.conditions, form.newCondition.trim()], newCondition: "" });
                        }
                      }}
                      data-testid="add-condition-btn"
                    >Add</Button>
                  </div>
                  {form.conditions.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {form.conditions.map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                          <span>✓ {c}</span>
                          <button onClick={() => setForm({ ...form, conditions: form.conditions.filter((_, idx) => idx !== i) })} className="text-red-700">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {decision?.mode === "reject" && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500">
                  Alternative Suggestion (optional)
                </label>
                <Textarea
                  value={form.alternative}
                  onChange={(e) => setForm({ ...form, alternative: e.target.value })}
                  rows={2}
                  placeholder="E.g., Suggest: Upgrade RAM in existing laptop. Cost: ₹10,000."
                  className="mt-1"
                  data-testid="alternative-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision(null)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={busy || form.remarks.trim().length < 3}
              className={decision?.mode === "approve" ? "brand-bg brand-bg-hover text-white" : "bg-red-600 hover:bg-red-700 text-white"}
              data-testid="confirm-decision-btn"
            >
              {busy ? "Submitting…" : decision?.mode === "approve" ? "Confirm Approval" : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
